// @ts-strict-ignore
import { MultiSelect } from '@usealloy/component-library';
import React, { forwardRef } from 'react';

import type { Action } from 'flow/store/engine/types';
import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import { replaceDefaultWorkflowRoutingThunk } from 'flow/store/redux/thunks';
import {
  selectWorkflowProps,
  selectWorkflowRoutingActions,
} from 'flow/store/redux/selectors';
import { updateAction } from 'flow/store/redux/slice';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { buildWorkflowRoutingOptions } from './helpers';
import type { WorkflowRoutingOption } from './types';
import SingleValue from './SingleValue';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';


interface WorkflowRoutingProps {
  isDefaultAction?: boolean;
  action: Action;
  dataTestId?: string;
}

const WorkflowRoutingDropdown = forwardRef<any, WorkflowRoutingProps>(({
  isDefaultAction = false,
  action,
  dataTestId,
}, ref) => {
  const dispatch = useAppDispatch();
  const { allWorkflows } = useAppSelector(selectWorkflowProps);
  const workflowRoutingActions = useAppSelector(selectWorkflowRoutingActions);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const workflowRoutingOptions = buildWorkflowRoutingOptions(allWorkflows, workflowRoutingActions);
  const [
    selectedWorkflowRoutingOption, setSelectedWorkflowRoutingOption,
  ] = React.useState<WorkflowRoutingOption>(() => workflowRoutingOptions
    .find(workflowRoutingOption => (
      workflowRoutingOption.meta.workflowId === action.workflow.workflowId),
    ) ?? null);

  const handleOnChange = React.useCallback((option: WorkflowRoutingOption) => {
    setSelectedWorkflowRoutingOption(option);

    if (isDefaultAction) {
      dispatch(replaceDefaultWorkflowRoutingThunk(option.meta.workflowId));
    } else {
      dispatch(updateAction({
        path: `${action.publicId}.workflow.name`,
        value: option.value,
      }));

      dispatch(updateAction({
        path: `${action.publicId}.workflow.workflowId`,
        value: option.meta.workflowId,
      }));
    }
  }, [dispatch, isDefaultAction, action]);

  return (
    <span
      data-testid={dataTestId}
      data-value={selectedWorkflowRoutingOption?.['data-value'] || ''}
    >
      <MultiSelect
        // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
        {...MULTISELECT_PROPS}
        ref={ref}
        isDisabled={!inEditMode}
        openMenuOnFocus
        customStyleFunctions={{
          ...MULTISELECT_PROPS.customStyleFunctions,
          container: styles => ({
            ...styles,
            margin: '0',
            minWidth: '80',
          }),
        }}
        options={workflowRoutingOptions}
        onChange={handleOnChange}
        selectedOptions={[selectedWorkflowRoutingOption]}
        // FIXME: BUILD-1368
        isOptionDisabled={(o: WorkflowRoutingOption) => o.disabled}
        components={{
          SingleValue,
        }}
      />
    </span>
  );
});

WorkflowRoutingDropdown.displayName = 'WorkflowRoutingDropdown';
export default WorkflowRoutingDropdown;
